import React, { useState, useEffect } from "react";
import { useParams, useNavigate,Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  User,
  Mail,
  Phone,
  Building,
  Calendar,
  Briefcase,
  Globe,
  MapPin,
  GraduationCap,
  Linkedin,
  Twitter,
  Github,
  Loader,
  ChevronLeft,
  Clock,
  Code,
  Shield,
  FileText,
  LinkIcon,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  RefreshCw,
} from "lucide-react";
// import AchievementsSection from "../components/AchievementsSection";
import {ConnectButton} from "../components/ConnectionComponents";
import GeminiAnalysis from "../components/GeminiAnalysis";

import COMPANY_LOGOS from "../constants/companyLogos";

const calculateTotalExperience = (experiences) => {
  let totalMonths = 0;
  experiences.forEach((exp) => {
    const start = new Date(exp.startDate);
    const end = exp.endDate ? new Date(exp.endDate) : new Date();
    totalMonths +=
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth());
  });
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  return months > 0 ? `${years} years, ${months} months` : `${years} years`;
};

const formatDate = (date) => {
  if (!date) return "Present";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
  });
};

const getYearsSinceGraduation = (graduationYear) => {
  const currentYear = new Date().getFullYear();
  if (graduationYear > currentYear) {
    return `Graduating in ${graduationYear}`;
  }
  const years = currentYear - graduationYear;
  return `${years} ${years === 1 ? "year" : "years"} since graduation`;
};

const MetricCard = ({ icon: Icon, label, value, className }) => (
  <div
    className={`bg-white rounded-xl shadow-sm p-6 flex items-center space-x-4 hover:shadow-md transition-all ${className}`}
  >
    <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
      <Icon className="h-6 w-6 text-blue-600" />
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  </div>
);

const CompanyLogo = ({ company, size = "medium" }) => {
  const sizeClasses = {
    small: "h-10 w-10",
    medium: "h-16 w-16",
    large: "h-20 w-20",
  };

  return (
    <div
      className={`${sizeClasses[size]} rounded-xl bg-white flex items-center justify-center p-3 border border-gray-100 shadow-sm hover:shadow-md transition-all`}
    >
      {COMPANY_LOGOS[company] ? (
        <img
          src={COMPANY_LOGOS[company]}
          alt={company}
          className="h-full w-full object-contain"
        />
      ) : (
        <Building
          className={`${
            size === "small" ? "h-6 w-6" : "h-8 w-8"
          } text-gray-400`}
        />
      )}
    </div>
  );
};

const ExperienceCard = ({ experience, isLast }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      className={`relative pl-8 ${
        !isLast && "pb-6 border-l-2 border-gray-200"
      }`}
    >
      <div className="absolute left-[-9px] top-0 w-4 h-4 bg-blue-500 rounded-full border-2 border-white" />

      <div className="bg-white rounded-xl shadow-sm p-4 hover:shadow-md transition-all">
        <div className="flex items-center space-x-4">
          <CompanyLogo company={experience.company} size="medium" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              {experience.title}
            </h3>
            <p className="text-gray-600">{experience.company}</p>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1.5" />
                {experience.location}
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1.5" />
                {formatDate(experience.startDate)} -{" "}
                {formatDate(experience.endDate)}
              </div>
            </div>
          </div>
          {experience.isCurrent && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Current
            </span>
          )}
        </div>

        {experience.description && (
          <div className="mt-4 text-gray-600 text-sm">
            {experience.description.split("\n").map((line, idx) => (
              <p key={idx} className="mb-2">
                {line}
              </p>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const AlumniDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [alumni, setAlumni] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
const [showAnalysis, setShowAnalysis] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchAlumniDetails();
  }, [id]);
  useEffect(() => {
    const fetchConnectionStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/connections/status/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setConnectionStatus(data.status);
        }
      } catch (err) {
        console.error('Error fetching connection status:', err);
      }
    };
  
    if (id) {
      fetchConnectionStatus();
    }
  }, [id]);
  useEffect(() => {
    const fetchCurrentUserProfile = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/alumni/me`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        if (!response.ok) throw new Error('Failed to fetch current user profile');
        const data = await response.json();
        setCurrentUserProfile(data);
      } catch (err) {
        console.error('Error fetching current user profile:', err);
      }
    };
  
    fetchCurrentUserProfile();
  }, []);
  const handleAnalyzeProfiles = () => {
    if (!currentUserProfile || !alumni) return;
    
    setShowAnalysis(true);
  };
  const fetchAlumniDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/alumni/profile/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch alumni profile");
      const data = await response.json();
      setAlumni(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAlumniStats = () => {
    if (!alumni) return {};

    const totalExperience = calculateTotalExperience(
      alumni.professionalInfo?.experiences || []
    );
    const yearsSinceGraduation = getYearsSinceGraduation(alumni.graduationYear);
    const companies = new Set(
      alumni.professionalInfo?.experiences?.map((exp) => exp.company) || []
    );
    const locations = new Set(
      alumni.professionalInfo?.experiences?.map((exp) => exp.location) || []
    );

    return {
      totalExperience,
      yearsSinceGraduation,
      companies: companies.size,
      locations: locations.size,
      currentCompany: alumni.professionalInfo?.experiences?.find(
        (exp) => exp.isCurrent
      )?.company,
      totalProjects: alumni.professionalInfo?.experiences?.length || 0,
      skills: alumni.professionalInfo?.skills?.length || 0,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-gray-600 animate-pulse">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !alumni) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
          <p className="text-red-500 font-medium">
            {error || "Profile not found"}
          </p>
          <button
            onClick={() => navigate("/directory")}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Directory
          </button>
        </div>
      </div>
    );
  }

  const stats = getAlumniStats();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative ">
        <div
          className="h-48 bg-cover bg-center rounded-lg"
          style={{
            backgroundImage: "url('https://i.ibb.co/zfWnYCB/image.png')",
          }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Profile Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <div className="p-6">
                <div className="flex flex-col items-center text-center">
                  <div className="relative">
                    <div className="h-40 w-40 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center border-4 border-white shadow-xl overflow-hidden">
                      {alumni.personalInfo?.avatar ? (
                        <img
                          src={alumni.personalInfo.avatar}
                          alt={`${alumni.firstName} ${alumni.lastName}`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <User className="h-16 w-16 text-blue-600" />
                      )}
                    </div>
                    {alumni.isVerified && (
                      <div className="absolute -bottom-1 right-2 bg-green-500 text-white p-1.5 rounded-full">
                        <CheckCircle2 className="h-6 w-6" />
                      </div>
                    )}
                  </div>

                  <h1 className="mt-4 text-2xl font-bold text-gray-900">
                    {alumni.firstName} {alumni.lastName}
                  </h1>

                  {stats.currentCompany && (
                    <div className="mt-2 flex items-center justify-center space-x-2">
                      <CompanyLogo
                        company={stats.currentCompany}
                        size="medium"
                      />
                      <span className="text-gray-600 text-xl font-bold">
                        {stats.currentCompany}
                      </span>
                    </div>
                  )}

<div className="mt-4 flex items-center justify-center space-x-2 text-sm text-gray-500">
  <GraduationCap className="h-4 w-4" />
  <span>{alumni.academicInfo?.degree}</span>
  <span>•</span>
  <span>{alumni.academicInfo?.department}</span>
  <span>•</span>
  <span>Class of {alumni.graduationYear}</span>
</div>

                  {alumni.enrollmentNumber && (
                    <div className="mt-2 text-sm text-gray-500">
                      ID: {alumni.enrollmentNumber}
                    </div>
                  )}

                  <div className="mt-6 flex justify-center space-x-3">
                    {alumni.socialLinks?.linkedin && (
                      <a
                        href={alumni.socialLinks.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-500 transition-colors"
                      >
                        <Linkedin className="h-5 w-5" />
                      </a>
                    )}
                    {alumni.socialLinks?.github && (
                      <a
                        href={alumni.socialLinks.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-900 transition-colors"
                      >
                        <Github className="h-5 w-5" />
                      </a>
                    )}
                    {alumni.socialLinks?.twitter && (
                      <a
                        href={alumni.socialLinks.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-400 transition-colors"
                      >
                        <Twitter className="h-5 w-5" />
                      </a>
                    )}
                    {alumni.socialLinks?.portfolio && (
                      <a
                        href={alumni.socialLinks.portfolio}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-purple-500 transition-colors"
                      >
                        <Globe className="h-5 w-5" />
                      </a>
                    )}
                  </div>

                  {/* Contact Actions */}
                  <div className="mt-6 w-full space-y-2">
                    {alumni.email && (
                      <a
                        href={`mailto:${alumni.email}`}
                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Send Email
                      </a>
                    )}
                  </div>
                  {!connectionStatus && currentUserProfile && alumni._id !== currentUserProfile._id && (
  <ConnectButton
    alumniId={id}
    onSuccess={() => setConnectionStatus('pending')}
  />
)}
                  {connectionStatus === 'pending' && (
  <button
    disabled
    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-500 bg-gray-100"
  >
    <Clock className="h-4 w-4 mr-2" />
    Request Pending
  </button>
)}
{connectionStatus === 'accepted' && (
  <Link
    to={`/chat/${id}`}
    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
  >
    <MessageSquare className="h-4 w-4 mr-2" />
    Message
  </Link>
)}
{connectionStatus === 'rejected' && (
  <button
    onClick={() => {
      setConnectionStatus(null);
    }}
    className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
  >
    <RefreshCw className="h-4 w-4 mr-2" />
    Send New Request
  </button>
)}

{currentUserProfile && alumni._id !== currentUserProfile._id && (
  <button
    onClick={handleAnalyzeProfiles}
    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium mt-4 text-white bg-blue-600 hover:bg-blue-700"
  >
    <Code className="h-4 w-4 mr-2" />
    Analyze Profile Compatibility
  </button>
)}
                </div>

                {/* Quick Stats */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Total Experience</span>
                      <span className="font-medium text-gray-900">
                        {stats.totalExperience}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Companies</span>
                      <span className="font-medium text-gray-900">
                        {stats.companies}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Skills</span>
                      <span className="font-medium text-gray-900">
                        {stats.skills}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Alumni Since</span>
                      <span className="font-medium text-gray-900">
                        {stats.yearsSinceGraduation} years
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            {/* Contact Information - Moved here */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Contact Details
              </h2>
              <div className="space-y-4">
                {alumni.user.email && (
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Primary Email</div>
                      <a
                        href={`mailto:${alumni.user.email}`}
                        className="font-medium text-blue-600 hover:text-blue-800"
                      >
                        {alumni.user.email}
                      </a>
                    </div>
                  </div>
                )}

                {alumni.personalInfo?.alternateEmail && (
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">
                        Alternate Email
                      </div>
                      <a
                        href={`mailto:${alumni.personalInfo.alternateEmail}`}
                        className="font-medium text-blue-600 hover:text-blue-800"
                      >
                        {alumni.personalInfo.alternateEmail}
                      </a>
                    </div>
                  </div>
                )}

                {alumni.personalInfo?.contactNumber && (
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Phone Number</div>
                      <a
                        href={`tel:${alumni.personalInfo.contactNumber}`}
                        className="font-medium text-gray-900"
                      >
                        {alumni.personalInfo.contactNumber}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Academic Background - Moved here */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Academic Background
              </h2>
              <div className="space-y-6">
                {/* College Info */}
                <div className="flex items-center space-x-4 pb-4 border-b border-gray-200">
                  <img
                    src={alumni.college.logo}
                    alt={alumni.college.name}
                    className="h-16 w-16 object-contain"
                  />
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {alumni.college.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {alumni.college.code}
                    </p>
                  </div>
                </div>

                {/* Academic Details */}
                <div className="space-y-4">
                

                  <div className="space-y-4">
      <div className="flex items-center space-x-3">
        <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
          <GraduationCap className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <div className="text-sm text-gray-500">Degree</div>
          <div className="font-medium text-gray-900">
            {alumni.academicInfo?.degree || "Not specified"}
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-3">
        <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
          <Building className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <div className="text-sm text-gray-500">Department</div>
          <div className="font-medium text-gray-900">
            {alumni.academicInfo?.department || "Not specified"}
          </div>
        </div>
      </div>
      </div>
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Duration</div>
                      <div className="font-medium text-gray-900">
                        {alumni.academicInfo?.batch?.startYear} -{" "}
                        {alumni.graduationYear}
                      </div>
                    </div>
                  </div>
                </div>
             
                {/* College Stats */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-900 mb-4">
                    College Stats
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-blue-600">
                        {alumni.college.stats.totalAlumni.toLocaleString()}+
                      </div>
                      <div className="text-xs text-gray-500">
                        Alumni Network
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-green-600">
                        {alumni.college.stats.placementRate}%
                      </div>
                      <div className="text-xs text-gray-500">
                        Placement Rate
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard
                icon={Briefcase}
                label="Professional Journey"
                value={`${stats.totalExperience}`}
              />
              <MetricCard
                icon={Building}
                label="Companies"
                value={stats.companies}
              />
              <MetricCard
                icon={MapPin}
                label="Locations"
                value={stats.locations}
              />
            </div>

            {/* Professional Experience */}
            {alumni.professionalInfo?.experiences?.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Professional Journey
                  </h2>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="h-4 w-4 mr-1.5" />
                    {stats.totalExperience} total
                  </div>
                </div>

                <div className="space-y-6">
                  {alumni.professionalInfo.experiences.map((exp, index) => (
                    <ExperienceCard
                      key={exp._id}
                      experience={exp}
                      isLast={
                        index === alumni.professionalInfo.experiences.length - 1
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Skills Section */}
            {alumni.professionalInfo?.skills?.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  Skills & Expertise
                </h2>
                <div className="flex flex-wrap gap-3">
                  {alumni.professionalInfo.skills.map((skill, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full flex items-center"
                    >
                      <Code className="h-4 w-4 mr-2" />
                      {skill}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Current Status and Overview */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Current Overview
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {alumni.professionalInfo?.currentlyEmployed && (
                    <div className="bg-green-50 text-green-700 rounded-lg p-4 flex items-center">
                      <Briefcase className="h-5 w-5 mr-3" />
                      <div>
                        <div className="font-medium">Currently Employed</div>
                        <div className="text-sm">{stats.currentCompany}</div>
                      </div>
                    </div>
                  )}

                  {alumni.enrollmentNumber && (
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">
                          Enrollment Number
                        </div>
                        <div className="font-medium text-gray-900">
                          {alumni.enrollmentNumber}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {alumni.isVerified && (
                    <div className="bg-blue-50 text-blue-700 rounded-lg p-4 flex items-center">
                      <Shield className="h-5 w-5 mr-3" />
                      <div>
                        <div className="font-medium">Verified Alumni</div>
                        <div className="text-sm">
                          Profile verified by institution
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Member Since</div>
                      <div className="font-medium text-gray-900">
                        {new Date(alumni.createdAt).toLocaleDateString(
                          "en-US",
                          {
                            month: "long",
                            year: "numeric",
                          }
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* {alumni.academicInfo?.achievements?.length > 0 && (
  <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
    <AchievementsSection 
      achievements={alumni.academicInfo.achievements.map(achievementString => {
        try {
          // Assuming parseAchievement is a function you've defined elsewhere
          // If not, you'll need to add this function
          return typeof achievementString === 'string'
            ? JSON.parse(achievementString)
            : achievementString;
        } catch (error) {
          console.error('Error parsing achievement:', error);
          return {
            title: achievementString,
            description: '',
            issueDate: '',
            certificateImage: ''
          };
        }
      })}
      isDetailed={true}
    />
  </div>
)} */}

          </div>
        </div>
        {showAnalysis && (
  <GeminiAnalysis
    onClose={() => setShowAnalysis(false)}
    title="Profile Analysis & Insights"
    initialPrompt={`Analyze these two alumni profiles and provide insights about:
      1. Career path similarities and differences
      2. Potential networking opportunities
      3. Skill overlap and complementary abilities
      4. Industry experience comparison
      5. Recommended conversation starters based on common background
      6. Potential mentorship opportunities

      Format the response in a clear, organized way with headings for each section.`}
    data={{
      viewedProfile: {
        name: `${alumni.firstName} ${alumni.lastName}`,
        graduation: alumni.graduationYear,
        department: alumni.academicInfo?.department,
        experiences: alumni.professionalInfo?.experiences,
        skills: alumni.professionalInfo?.skills,
      },
      currentUserProfile: {
        name: `${currentUserProfile?.firstName} ${currentUserProfile?.lastName}`,
        graduation: currentUserProfile?.graduationYear,
        department: currentUserProfile?.academicInfo?.department,
        experiences: currentUserProfile?.professionalInfo?.experiences,
        skills: currentUserProfile?.professionalInfo?.skills,
      }
    }}
  />
)}

        {/* Share Menu */}
        {showShareMenu && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full mx-4"
            >
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Share Profile
              </h3>
              <div className="space-y-4">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    setShowShareMenu(false);
                  }}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Copy Link
                </button>
                <button
                  onClick={() => {
                    window.open(
                      `mailto:?subject=Check out this alumni profile&body=${window.location.href}`
                    );
                    setShowShareMenu(false);
                  }}
                  className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Share via Email
                </button>
              </div>
              <button
                onClick={() => setShowShareMenu(false)}
                className="mt-4 w-full px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlumniDetails;
